'use client'

import { useMutation } from '@tanstack/react-query'
import { useAtom, useAtomValue } from 'jotai'
import { useLocale } from 'next-intl'
import { useCallback, useMemo } from 'react'

import { useAuth } from '@/features/auth/hooks'
import { useCardAccountContext, useSetCardAccountId } from '@/hooks/use-card-account'
import { useLinkingGroupState } from '@/hooks/use-linking-group-state'
import { usePreferredOrderAccountId } from '@/hooks/use-preferred-order-account'
import globalStore from '@/lib/configs/store-config'
import { parseExchangeField } from '@/lib/proto/util'
import { isOrderEligibleAccount } from '@/lib/utils/account-select'
import { useUserAccounts } from '@/service/account.hook'
import { sendChatMessage } from '@/service/ai.api'
import { chatIdAtomFamily } from '@/state/chat'
import {
  lastFocusedOrderCardAtom,
  orderFormOrderbookSignalAtomFamily,
  orderFormSelectionAtomFamily,
} from '@/state/order'
import { positionTableTabByInstanceAtom } from '@/state/widget-instances'
import { historySectionOf, PositionsTabValue, PositionTableTab } from '@/types/dashboard'
import {
  ActiveTab,
  ChatResponse,
  OrderbookClick,
  PositionFilters,
  SelectedInstrument as ProtoInstrument,
  UIContext,
} from '@/types/generated/chat_api'
import { Account } from '@/types/generated/common/account'
import { Denomination, OrderSide } from '@/types/generated/common/trading'
import { Exchange, InstrumentKind } from '@/types/generated/common/types'
import { ErrorResponse } from '@/types/service'

function toInstrumentKind(type: string): InstrumentKind {
  switch (type) {
    case 'options':
      return InstrumentKind.INSTRUMENT_KIND_OPTION
    case 'future':
    case 'perps':
      return InstrumentKind.INSTRUMENT_KIND_FUTURE
    default:
      return InstrumentKind.INSTRUMENT_KIND_SPOT
  }
}

// Exported for __tests__/ai-hook-active-tab.test.ts only — every other
// caller should keep using it through useUIContextBuilder below.
export function toActiveTab(tab: PositionsTabValue): ActiveTab {
  // A promoted History-section tab (Orders/Hedges/Triggers/RFQs/Arb/Spreader,
  // see positions-tab-contents.tsx's "expanded" mode) represents the same
  // "looking at historical activity" state as UNIFIED_HISTORY below — without
  // this the switch's `default:` branch would silently report it as
  // "Positions" to the AI backend instead.
  if (historySectionOf(tab)) return ActiveTab.ACTIVE_TAB_HISTORY
  switch (tab) {
    case PositionTableTab.ACTIVE:
      return ActiveTab.ACTIVE_TAB_ORDERS
    case PositionTableTab.HISTORY:
    // The backend's ActiveTab enum has no dedicated "unified log" value and
    // proto changes are out of scope for this phase (see Global Constraints).
    // ACTIVE_TAB_HISTORY is the closest existing match — both this tab and
    // the Trades tab represent "the user is looking at historical, not
    // live/pending, activity" from the AI backend's point of view.
    case PositionTableTab.UNIFIED_HISTORY:
      return ActiveTab.ACTIVE_TAB_HISTORY
    default:
      return ActiveTab.ACTIVE_TAB_POSITIONS
  }
}

// These atoms live on the global store. Pin them explicitly via { store } so this still
// resolves correctly when called from inside a scoped JotaiProvider (e.g. the order-form
// popup's isolated chat store) — otherwise reads silently fall back to that empty store.
export function useUIContextBuilder(cardId: string = '', positionInstanceId = 'positions-1') {
  // Trading identity is resolved from this card's own focused watch-list tab (same
  // mechanism as position-table/watchlist-view/order-form), not the global
  // exchangeAtom — so a chat card on one linking-color group reflects that group's
  // own account/venue instead of whatever the top-nav selector happens to show.
  const { tradeVenue: cardTradeVenue, cardTradingSelection } = useCardAccountContext(cardId)
  const { data: userAccountsData } = useUserAccounts({ showDisabled: true })
  const accounts = useMemo(() => userAccountsData?.accounts ?? [], [userAccountsData?.accounts])
  const { watchList, selectedTicker, positionFilter } = useLinkingGroupState(cardId)
  const [{ selectedInstruments, selectedOptions }, setSelectionAtom] = useAtom(
    orderFormSelectionAtomFamily(cardId),
    { store: globalStore }
  )
  const { price: orderbookPrice, amount: orderbookAmount } = useAtomValue(
    orderFormOrderbookSignalAtomFamily(cardId),
    { store: globalStore }
  )
  const positionTab = useAtomValue(positionTableTabByInstanceAtom(positionInstanceId), {
    store: globalStore,
  })

  // Signal 1a — instruments selected via shift-click / bid-ask in watchlist
  const primaryInstrument = selectedInstruments[0] ?? null
  // Signal 1b — options selected in option chain widget
  const primaryOption = selectedOptions[0] ?? null

  // A shift-clicked/bid-ask-selected instrument already carries its own resolved
  // accountId (stamped at selection time by watchlist-view.tsx for the row's own
  // exchange — see resolveAccountIdForExchange). That seed wins over this card's
  // own tab-pinned account by default (the instrument may live on a different
  // venue than the focused tab), but the user can still pick a different account
  // on that same venue below — see setAccountId.
  const selectionAccountId = primaryInstrument?.accountId ?? primaryOption?.accountId
  const isAccountOverriddenBySelection = Boolean(selectionAccountId)
  // The last account explicitly picked in the Order Form for this venue wins over
  // the card's own tab-pinned default — same chain as Quick Order/One-Tap/Percept,
  // not the card's raw derived default.
  const preferredAccountId = usePreferredOrderAccountId(
    cardTradeVenue,
    cardTradingSelection?.accountId
  )
  const effectiveAccountId = selectionAccountId ?? preferredAccountId
  const account: Account | null = accounts.find((a) => a.accountId === effectiveAccountId) ?? null
  const resolvedTradeVenue = account?.tradeVenue ?? cardTradeVenue

  // Accounts the manual account switcher can offer — same venue as what's
  // currently in effect, order-eligible only (never offer inactive/read-only accounts).
  const venueAccounts = useMemo(
    () =>
      accounts.filter(
        (account) => account.tradeVenue === resolvedTradeVenue && isOrderEligibleAccount(account)
      ),
    [accounts, resolvedTradeVenue]
  )

  const setFocusedTabAccountId = useSetCardAccountId(cardId)
  // Repoints whichever thing is actually driving `account` right now: the
  // selected instrument/option's own accountId if one is selected (so a manual
  // pick sticks to that specific selection, same as its auto-seeded value did),
  // else the card's own focused tab — matching effectiveAccountId's precedence.
  const setAccountId = useCallback(
    (accountId: string) => {
      if (primaryInstrument) {
        setSelectionAtom((prev) => ({
          ...prev,
          selectedInstruments: prev.selectedInstruments.map((inst, i) =>
            i === 0 ? { ...inst, accountId } : inst
          ),
        }))
        return
      }
      if (primaryOption) {
        setSelectionAtom((prev) => ({
          ...prev,
          selectedOptions: prev.selectedOptions.map((opt, i) =>
            i === 0 ? { ...opt, accountId } : opt
          ),
        }))
        return
      }
      setFocusedTabAccountId(accountId)
    },
    [primaryInstrument, primaryOption, setSelectionAtom, setFocusedTabAccountId]
  )

  // Same derivation as order-form-context and selected-snack
  const selectedSymbol =
    watchList.selectedIndex >= 0 ? watchList.list[watchList.selectedIndex] : null

  const buildContext = (): UIContext => {
    // focusedSymbol: order-form instrument > option selection > chart context
    const focusedSymbolStr =
      primaryInstrument?.symbol ??
      primaryOption?.instrumentName ??
      selectedSymbol?.symbol ??
      selectedTicker?.symbol ??
      ''

    // focusedAsset: baseCurrency from the selected instrument, else parse from symbol
    const focusedAssetStr =
      primaryInstrument?.baseCurrency ??
      (focusedSymbolStr ? focusedSymbolStr.split(/[-_]/)[0] : '') ??
      selectedTicker?.baseCurrency ??
      ''

    // focusedType: instrument kind of what the user is working with
    const focusedTypeEnum = primaryInstrument
      ? toInstrumentKind(primaryInstrument.instrumentKind)
      : primaryOption
        ? InstrumentKind.INSTRUMENT_KIND_OPTION
        : (selectedSymbol ?? selectedTicker)
          ? toInstrumentKind((selectedSymbol ?? selectedTicker)!.type)
          : InstrumentKind.INSTRUMENT_KIND_UNSPECIFIED

    // focusedExchange: from selected instrument, no fallback (chart ticker has no exchange field)
    const focusedExchangeEnum = parseExchangeField(
      primaryInstrument?.exchange,
      Exchange.EXCHANGE_UNSPECIFIED
    )

    // Option protos — from option chain selection
    const optionProtos: ProtoInstrument[] = selectedOptions.map((opt) => ({
      symbol: opt.instrumentName,
      exchange: Exchange.EXCHANGE_UNSPECIFIED,
      lastPrice: opt.price ?? '',
      side: opt.tradeSide === 'buy' ? OrderSide.ORDER_SIDE_BUY : OrderSide.ORDER_SIDE_SELL,
    }))

    // Order form protos — from shift-click / bid-ask selection
    const orderProtos: ProtoInstrument[] = selectedInstruments.map((inst) => ({
      symbol: inst.symbol,
      exchange: parseExchangeField(inst.exchange, Exchange.EXCHANGE_UNSPECIFIED),
      lastPrice: inst.lastPrice ?? '',
      side: inst.side === 'buy' ? OrderSide.ORDER_SIDE_BUY : OrderSide.ORDER_SIDE_SELL,
    }))

    // Final order: options (primary intent) > order-form instruments
    // Chart context is already conveyed via focusedSymbol — no fallback injection here
    const protoInstruments: ProtoInstrument[] = [...optionProtos, ...orderProtos]

    const orderbookClick: OrderbookClick | undefined =
      orderbookPrice && orderbookAmount
        ? { price: orderbookPrice, amount: orderbookAmount, denom: Denomination.DENOMINATION_BASE }
        : undefined

    const parseFilter = (vals?: string[] | null) => {
      if (!vals || vals.includes('ALL')) return []
      return vals
    }

    const positionFilters: PositionFilters | undefined = positionFilter
      ? {
          tickers: parseFilter(positionFilter.assets?.value),
          expiries: parseFilter(positionFilter.expiries?.value),
        }
      : undefined

    const context: UIContext = {
      tradeVenue: resolvedTradeVenue ?? 0,
      focusedExchange: focusedExchangeEnum,
      focusedSymbol: focusedSymbolStr,
      focusedAsset: focusedAssetStr,
      focusedType: focusedTypeEnum,
      selectedInstruments: protoInstruments,
      orderbookClick,
      activeTab: toActiveTab(positionTab),
      positionFilters,
      // chartOption/timeframe are now per-chart-widget, not per-linking-color, so
      // there's no principled single chart to attribute this to from a chat/order-form
      // card's cardId (a color group can contain zero, one, or several charts with
      // independent settings). '' reads as "unknown" — proto3 omits empty strings from
      // the wire payload, and the field is analytics-only, never injected into the prompt.
      chartTimeframe: '',
    }

    // console.log('[UIContext]', {
    //   account: account?.accountId,
    //   tradeVenue: account?.tradeVenue,
    //   focusedSymbol: context.focusedSymbol,
    //   focusedAsset: context.focusedAsset,
    //   focusedType: context.focusedType,
    //   selectedInstruments: context.selectedInstruments,
    //   selectedOptions: selectedOptions.map((o) => ({ name: o.instrumentName, side: o.side })),
    //   orderbookClick: context.orderbookClick,
    //   activeTab: context.activeTab,
    //   positionFilters: context.positionFilters,
    //   chartTimeframe: context.chartTimeframe,
    // })

    return context
  }

  return {
    account,
    isAccountOverriddenBySelection,
    venueAccounts,
    setAccountId,
    buildContext,
  }
}

export const useSendChatMessage = (props?: {
  // Explicit cardId of the chat instance sending the message (e.g. the order-form
  // popup's own cardId). Takes priority over lastFocusedOrderCardAtom because the
  // popup renders its <Chat> inside an isolated Jotai store (order-form-sheet.tsx)
  // — lastFocusedOrderCardAtom written by the dashboard's OrderContainer never
  // reaches that scoped store, so falling back to it silently resolves to the
  // wrong (or empty) cardId and the chat context misses the user's actual selection.
  cardId?: string
  threadId: string
  onSuccess?: (data: ChatResponse) => void
  onError?: (error: ErrorResponse) => void
}) => {
  const { accessToken } = useAuth()
  const locale = useLocale()
  const lastFocusedCard = useAtomValue(lastFocusedOrderCardAtom, { store: globalStore })
  const { account, buildContext } = useUIContextBuilder(props?.cardId ?? lastFocusedCard ?? '')
  const [chatId, setChatId] = useAtom(chatIdAtomFamily(props?.threadId ?? 'thread:default'))

  const mutation = useMutation({
    // A bare prompt sends under the card-resolved account. `{ prompt, account }` sends this one
    // message under another account (the Order Form prompt bar's @mention) without repointing the
    // card's own selection; the context's venue follows that account.
    mutationFn: (input: string | { prompt: string; account?: Account | null }) => {
      const { prompt, account: override } =
        typeof input === 'string' ? { prompt: input, account: null } : input
      const context = buildContext()
      const sendAccount = override ?? account
      return sendChatMessage({
        accountId: sendAccount?.accountId ?? '',
        prompt,
        context: override ? { ...context, tradeVenue: override.tradeVenue } : context,
        sessionId: chatId,
        locale,
      })
    },
    onSuccess: (data) => {
      setChatId(data.sessionId)
      props?.onSuccess?.(data)
    },
    onError: props?.onError,
    meta: { accessToken },
  })

  // Expose the same card-resolved account the mutation submits under, so callers
  // (e.g. use-multimodal-input-state.ts) build execution-plan/order-card display
  // data off the same account instead of independently re-reading a different source.
  return { ...mutation, account }
}
